import React, { FC, useEffect, useState } from 'react';
import { ApolloError, useLazyQuery, useMutation, NetworkStatus } from '@apollo/client';
import { find, get } from 'lodash';
import {
  Avatar,
  Box,
  Button,
  ButtonAppearance,
  ButtonSize,
  Combobox,
  FormControl,
  Heading,
  Icon,
  IconSize,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetContentCol,
  SideSheetFooter,
  SideSheetHeader,
  SideSheetSubHeader,
  Spinner,
  SpinnerSize,
  Text,
  TextArea,
  TextFieldSize,
  ToastAction,
  ToastType,
  useModalState,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  AssignWeldForemanError,
  AssignWeldForemanInput,
  AssignWeldForemanPayload,
  CrewMember,
  CrewMemberDiscipline,
  CrewMemberList,
  PagingInput,
  PersonaType,
  SearchCrewMemberInput,
} from '@bechtel/types';
import { Field, Form, Formik } from 'formik';
import { useDebounce, DrawerFooterButtonStyled, useName, ProjectAppTypes } from '@bechtel/client-common';
import { GET_CREW_MEMBERS, ASSIGN_WELDING_FOREMAN } from '@bechtel/client-gql';
import { useCommodity } from '~/detail/commodities/context';
import { useWorkPackage } from '~/detail/context';
import { AssignWeldError } from '../error';
import { AddCrewStyled } from '../styled';
import { t, Trans } from '@lingui/macro';

const UserListItem = ({ item }: any) => {
  const { getFullName } = useName();
  const userName = !item.firstName ? t`Unassigned` : getFullName(item.firstName, item.lastName);
  return (
    <Box flexDirection={{ sm: 'row' }} alignItems={{ sm: 'center' }}>
      <Box mr={{ sm: '400' }}>
        {item.imageUrl ? (
          <Avatar data-testid="avatar-regular-owner" src={item.imageUrl} />
        ) : (
          <AddCrewStyled
            css={`
              ${({ theme }) => `background-color: ${theme.colors.grey[300]};
              border: none;`}
            `}
          >
            <Icon fillColor="white" size={IconSize.MEDIUM} icon="user" iconStyle="solid" />
          </AddCrewStyled>
        )}
      </Box>
      <Text size="450">{userName}</Text>
    </Box>
  );
};

interface Values {
  comment: string;
  assigned: CrewMember;
}

const initialValues: Values = {
  comment: '',
  assigned: {
    id: '',
    firstName: '',
    lastName: '',
  },
};

interface WeldAssignProps {
  close: () => void;
  existingAssignment: CrewMember | undefined | null;
}

const createSuccessToast: ToastAction = {
  type: 'ADD_TOAST',
  payload: {
    toastType: ToastType.SUCCESS,
    content: t`User successfully assigned to weld`,
    autoDismissTimeout: 5000,
  },
};

const foremanUnassignedToast: ToastAction = {
  type: 'ADD_TOAST',
  payload: {
    toastType: ToastType.SUCCESS,
    content: t`User successfully unassigned from weld`,
    autoDismissTimeout: 5000,
  },
};

export const WeldAssign: FC<WeldAssignProps> = ({ close, existingAssignment }) => {
  const { getFullName } = useName();
  const wp = useWorkPackage();
  const commodity = useCommodity();
  const dispatchToast = useToastsDispatch();
  const { close: closeModal, open: openErrorModal } = useModalState(false);
  const [assignWeldForemanError, setAssignWeldForemanError] = useState<AssignWeldForemanError | ApolloError | null>();
  const [searchText, setSearchText] = useState('');
  const debouncedSearchText = useDebounce(searchText, 500);
  const itemToString = (item) => getFullName(item.firstName, item.lastName);
  const unAssign: CrewMember = {
    id: '',
    imageUrl: '',
    firstName: t`Unassign`,
    lastName: '',
    persona: PersonaType.FOREMAN,
  };
  const [selection, setSelection] = useState(existingAssignment ? existingAssignment : unAssign);
  // TODO: ask BE if we can add comments to the assignWeldForeman mutation.
  const [assignWeldForeman, { loading: mutationFiring }] = useMutation<
    { assignWeldForeman: AssignWeldForemanPayload },
    { input: AssignWeldForemanInput }
  >(ASSIGN_WELDING_FOREMAN, {
    onCompleted: (data) => {
      const assignError = get(data, 'assignWeldForeman.error');
      if (assignError) {
        setAssignWeldForemanError(assignError);
        openErrorModal();
      } else {
        close();
        const assignForemanWeld = data.assignWeldForeman.commodity[0]?.weld;
        const commodityAssignee =
          assignForemanWeld &&
          find(assignForemanWeld.commodityAssignees, (item) => {
            return item.personaType == PersonaType.FOREMAN;
          });
        dispatchToast(!commodityAssignee ? foremanUnassignedToast : createSuccessToast);
      }
    },
    onError: (error) => {
      setAssignWeldForemanError(error);
      openErrorModal();
    },
  });

  const [getCrewMembers, { data, error, loading, refetch, networkStatus }] = useLazyQuery<
    { crewMembers: CrewMemberList },
    { input: SearchCrewMemberInput; paging: PagingInput }
  >(GET_CREW_MEMBERS, {
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    if (debouncedSearchText?.length > 0 && debouncedSearchText?.length < 3) return;
    getCrewMembers({
      variables: {
        input: {
          searchKeyword: debouncedSearchText,
          persona: PersonaType.FOREMAN,
          appTypeId: ProjectAppTypes.AS
        },
        paging: {
          cursor: '1',
          limit: 30,
        },
      },
    });
  }, [debouncedSearchText, getCrewMembers]);

  const crewMembersList = get(data, 'crewMembers.items', []) as CrewMemberList['items'];
  const crewMembersListWithUnAssign = crewMembersList.reduce(
    (crewMember, item) => {
      return crewMember.concat(item);
    },
    [unAssign]
  );

  const closeErrorModal = () => {
    setAssignWeldForemanError(null);
    closeModal();
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values) => {
        const assignWeldForemanInput: AssignWeldForemanInput = {
          workPackageId: wp.id,
          weldForemanInput: [{ foremanId: values.assigned.id, commodityId: commodity.id, typeId: commodity.typeId }],
        };
        await assignWeldForeman({
          variables: {
            input: assignWeldForemanInput,
          },
        });
      }}
    >
      {({ dirty, handleSubmit, isValid, setFieldValue }) => (
        <Form>
          <SideSheet visible onDismiss={close} data-testid="assign-weld-sidesheet">
            <SideSheetHeader title={t`Assign Welding Foreman`}>
              <SideSheetClose onClick={close}></SideSheetClose>
            </SideSheetHeader>

            <SideSheetSubHeader>
              <Box mb={{ sm: '400' }}>
                <Heading>
                  <Trans>WorkPackage</Trans>:&nbsp;
                </Heading>
                {wp.tag}
              </Box>
              <Box>
                <Heading>{commodity.typeName}:&nbsp;</Heading>
                {commodity.tag}
              </Box>
            </SideSheetSubHeader>

            <SideSheetBody>
              <SideSheetContentCol>
                <Box py={{ sm: '700' }} flexDirection={{ sm: 'column' }}>
                  {!loading && error && (
                    <Box px={{ sm: '500' }} css="height: 100%;">
                      <Box
                        flexGrow={{ sm: 1 }}
                        alignItems={{ sm: 'center' }}
                        flexDirection={{ sm: 'column' }}
                        justifyContent={{ sm: 'center' }}
                      >
                        <Box pb={{ sm: '500' }} flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }}>
                          <Text>
                            <Trans>There was a problem loading welding foreman(s)</Trans>
                          </Text>
                          <Text>
                            <Trans>Please click the button below to try again.</Trans>
                          </Text>
                        </Box>
                        <Box>
                          <Button size={ButtonSize.LARGE} onClick={() => refetch()}>
                            <Box mr={{ sm: '400' }}>
                              <Icon icon="undo" />
                            </Box>
                            <Trans> Retry</Trans>
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {!error && (
                    <Box
                      px={{ sm: '500' }}
                      css={`
                        ${({ theme }) => `border-bottom: 1px solid ${theme.colors.grey[300]};`}
                      `}
                      pb={{ sm: '700' }}
                    >
                      <Box flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
                        <Box mb={{ sm: '700' }}>
                          <FormControl label={t`Assigned To`} style={{ flexGrow: 1 }}>
                            <Combobox
                              ariaLabel="weld-foreman"
                              size={TextFieldSize.XLARGE}
                              items={existingAssignment ? crewMembersListWithUnAssign : crewMembersList}
                              itemComponent={UserListItem}
                              selectedComponent={UserListItem}
                              term={searchText}
                              setTerm={setSearchText}
                              placeholder={t`Search Weld Foreman`}
                              itemString={itemToString}
                              selectedItem={selection}
                              onSelect={(item: any) => {
                                setFieldValue('assigned', item);
                                setSelection(item);
                              }}
                              handleChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                setSearchText(event.target.value);
                              }}
                              loading={loading || networkStatus === NetworkStatus.refetch}
                              restorePreviousOnSkip
                              messageOnNoResults={t`No results found`}
                            />
                          </FormControl>
                        </Box>
                        <FormControl label={t`Comment`}>
                          <Field name="comment">
                            {({ field }) => {
                              return (
                                <TextArea
                                  {...field}
                                  placeholder={t`Add a comment (optional)`}
                                  rows="3"
                                  maxLength={2000}
                                />
                              );
                            }}
                          </Field>
                        </FormControl>
                      </Box>
                    </Box>
                  )}
                </Box>
              </SideSheetContentCol>
            </SideSheetBody>

            <SideSheetFooter>
              <Box justifyContent={{ sm: 'flex-end' }}>
                <Box mr={{ sm: '400' }}>
                  <DrawerFooterButtonStyled appearance={ButtonAppearance.SECONDARY} onClick={close}>
                    <Trans> Cancel</Trans>
                  </DrawerFooterButtonStyled>
                </Box>
                <Box>
                  <DrawerFooterButtonStyled
                    appearance={ButtonAppearance.SECONDARY}
                    onClick={() => handleSubmit()}
                    disabled={loading || mutationFiring || !dirty || !isValid}
                  >
                    {mutationFiring ? <Spinner size={SpinnerSize.SMALL} /> : <Trans>Assign</Trans>}
                  </DrawerFooterButtonStyled>
                </Box>
              </Box>
            </SideSheetFooter>
          </SideSheet>
          {assignWeldForemanError && (
            <AssignWeldError assignWeldForemanError={assignWeldForemanError} close={closeErrorModal} />
          )}
        </Form>
      )}
    </Formik>
  );
};
